import { useState, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
import { AuthPage } from './pages/AuthPage';
import { Lobby } from './pages/Lobby';
import { GamePage } from './pages/GamePage';
import { ProfilePage } from './pages/ProfilePage';
import './App.css';

// ─── Page State ─────────────────────────────────────────────────────────────

type AppPage = 'lobby' | 'game' | 'profile';

// ─── App Component ──────────────────────────────────────────────────────────

function App() {
  const { isAuthenticated, isLoading, checkAuth } = useAuth();
  const [currentPage, setCurrentPage] = useState<AppPage>('lobby');

  const navigateToGame = useCallback(() => setCurrentPage('game'), []);
  const navigateToProfile = useCallback(() => setCurrentPage('profile'), []);
  const navigateToLobby = useCallback(() => setCurrentPage('lobby'), []);

  // Loading state while auth initializes
  if (isLoading) {
    return (
      <div className="app" role="status" aria-label="Loading application">
        <div className="app__loading">
          <div className="app__loading-mushroom" aria-hidden="true">🍄</div>
          <p className="app__loading-text">Entering the forest...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — show auth page
  if (!isAuthenticated) {
    return (
      <div className="app">
        <div className="app__decorative-spore app__decorative-spore--1" aria-hidden="true" />
        <div className="app__decorative-spore app__decorative-spore--2" aria-hidden="true" />
        <div className="app__decorative-spore app__decorative-spore--3" aria-hidden="true" />
        <main className="app__content">
          <AuthPage onLoginSuccess={checkAuth} />
        </main>
      </div>
    );
  }

  // Authenticated — render appropriate page
  function renderPage() {
    switch (currentPage) {
      case 'game':
        return <GamePage onExit={navigateToLobby} />;
      case 'profile':
        return <ProfilePage onBack={navigateToLobby} />;
      case 'lobby':
      default:
        return <Lobby onPlay={navigateToGame} onProfile={navigateToProfile} />;
    }
  }

  return (
    <div className="app">
      <div className="app__decorative-spore app__decorative-spore--1" aria-hidden="true" />
      <div className="app__decorative-spore app__decorative-spore--2" aria-hidden="true" />
      <div className="app__decorative-spore app__decorative-spore--3" aria-hidden="true" />
      <main className="app__content">
        {renderPage()}
      </main>
    </div>
  );
}

export default App;
